import { formatCurrency, formatNumber } from "@angular/common";
import { ChartJsConfigBuilder, DeepPartial, Series } from "@tylertech/chart-js";
import { getLoggerFor, ILogger, LogInvocation, LogLevel } from "@tylertech/logging";
import { ICategoryChartData, IMetadataChart, IMetadataChartDataPoint, IPieChartData, ChartType as MetadataChartType } from "@tylertech/tyler-api-types";
import { ChartConfiguration, ChartConfigurationCustomTypesPerDataset, ChartType as ChartJsType, Color, ScaleOptionsByType, TooltipItem } from "chart.js";
import { isStacked } from "./chartHelpers";

function getBrushes(singleHue: boolean): string[]
{
    if (singleHue)
    {
        return ["#003D3C", "#008F8C"];
    }

    return [
        "#008F8C",
        "#0D2644",
        "#E36039",
        "#245718",
        "#5C6E83",
        "#641313",
        "#908939",
        "#5C4170",
        "#C63D3D",
        "#003D3C"
    ];
}

export class MetadataChartConverter
{
    private readonly log: ILogger;
    private readonly brushes: ReadonlyArray<Color>;
    constructor(
        private readonly model: Readonly<IMetadataChart>,
        private readonly locale: string,
        brushes?: ReadonlyArray<Color>)
    {
        this.log = getLoggerFor("MetadataChartConverter",
            () => `MetadataChartConverter("${this.model.Title}")`);

        this.brushes = brushes
            ? [...brushes]
            : getBrushes(false);

    }

    @LogInvocation(LogLevel.DEBUG)
    convertChart(): ChartConfiguration | ChartConfigurationCustomTypesPerDataset
    {
        const chartType = this.getChartJsType(this.model);
        const chartBuilder = new ChartJsConfigBuilder(this.model.Title, chartType)
            .setBorderColorSet(this.brushes)
            .setBackgroundColorSet(this.brushes)
            .setResponsive(true)
            .setMaintainAspectRatio(false)
            .setTitle({
                display: !!this.model.Title,
                text: this.model.Title,
            })
            .setLegend({
                display: this.model.HasLegend,
                position: "top",
                align: "start",
                // labels: {
                //     padding: 16,
                // },
            })
            .setTooltips({
                position: "average",
                callbacks: {
                    label: (tooltipItem: TooltipItem<ChartJsType>): string | string[] =>
                    {
                        const dataSetLabel = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].label;
                        const value = tooltipItem.raw as number;
                        const amount = this.model.IsCurrency
                            ? formatCurrency(value, this.locale, "$")
                            : formatNumber(value, this.locale);
                        return `${dataSetLabel}: ${amount}`;
                    },
                },
            });

        switch (chartType)
        {
            case "pie":
                return this.buildPieChart(this.model as IPieChartData, chartBuilder as ChartJsConfigBuilder<IMetadataChartDataPoint, "pie">);
            case "bar":
                return this.model.Type === MetadataChartType.Column
                    ? this.buildBarChart(this.model as ICategoryChartData, chartBuilder as ChartJsConfigBuilder<Record<string, number>, "bar">)
                    : this.buildHorizontalBarChart(this.model as ICategoryChartData, chartBuilder as ChartJsConfigBuilder<Record<string, number>, "bar">);
            case "line":
                return this.buildLineChart(this.model as ICategoryChartData, chartBuilder as ChartJsConfigBuilder<Record<string, number>, "line">);
            default:
                this.log.error(`unsupported chart type`, { type: chartType ?? this.model.Type, model: this.model });
                throw new Error(`unsupported chart type; was "${chartType ?? this.model.Type}"`);
        }

    }

    @LogInvocation(LogLevel.DEBUG)
    protected buildPieChart(pieChart: IPieChartData, builder: ChartJsConfigBuilder<IMetadataChartDataPoint, "pie">): ChartConfiguration<"pie">
    {
        return builder
            .addSeries({
                type: "pie",
                name: pieChart.Title,
                getValue: (v, _i) => v.Value,
            } satisfies Series<IMetadataChartDataPoint, "pie">)
            .setCategories(pieChart.Data.map(x => x.Label))
            .build(pieChart.Data) as ChartConfiguration<"pie">;
    }

    @LogInvocation(LogLevel.DEBUG)
    protected buildBarChart(barChart: ICategoryChartData, builder: ChartJsConfigBuilder<Record<string, number>, "bar">): ChartConfiguration<"bar">
    {
        const data = this.transformCategoryData(barChart);
        return builder
            .addScales({
                x: {
                    type: "category",
                    labels: barChart.Series[0].Data.map(x => x.Label),
                    stacked: isStacked(barChart)
                        ? barChart.Stacked
                        : false,
                } satisfies DeepPartial<ScaleOptionsByType<"category">>,
                y: {
                    type: "linear",
                    min: barChart.AxisMin
                        ?? Math.min(
                            ...data.flatMap(x =>
                            {
                                return Object.values(x);
                            })),
                    max: this.getMaximum(barChart, data),
                    stacked: isStacked(barChart)
                        ? barChart.Stacked
                        : false,
                    // TODO(DO-8111) - support formatting ticks as currency
                } satisfies DeepPartial<ScaleOptionsByType<"linear">>,
            })
            .addSeries(
                barChart.Series?.map(
                    series =>
                    {
                        return {
                            name: series.Name,
                            type: "bar",
                            getValue: (v, _i) => v[series.Name] ?? 0,
                            xAxisId: "x",
                            yAxisId: "y",
                        } satisfies Series<Record<string, number>, "bar">;
                    }) ?? [])
            .setCategories(barChart.Series?.length
                ? barChart.Series[0].Data.map(x => x.Label)
                : [])
            .build(data) as ChartConfiguration<"bar">;
    }

    @LogInvocation(LogLevel.DEBUG)
    protected buildHorizontalBarChart(barChart: ICategoryChartData, builder: ChartJsConfigBuilder<Record<string, number>, "bar">): ChartConfiguration<"bar">
    {
        const data = this.transformCategoryData(barChart);
        return builder
            .setIndexAxis("y")
            .addScales(
                {
                    x: {
                        min: barChart.AxisMin
                            ?? Math.min(
                                ...data.flatMap(x =>
                                {
                                    return Object.values(x);
                                })),
                        max: this.getMaximum(barChart, data),
                        stacked: isStacked(barChart)
                            ? barChart.Stacked
                            : false,
                        // TODO(DO-8111) - support formatting ticks as currency
                    } satisfies DeepPartial<ScaleOptionsByType<"linear">>,
                    y: {
                        labels: barChart.Series?.length
                            ? barChart.Series[0].Data.map(x => x.Label)
                            : undefined,
                        stacked: isStacked(barChart)
                            ? barChart.Stacked
                            : false,
                        // TODO(DO-8111) - support formatting ticks as currency
                    } satisfies DeepPartial<ScaleOptionsByType<"category">>,
                })
            .addSeries(
                barChart.Series?.map(
                    series =>
                    {
                        return {
                            name: series.Name,
                            type: "bar",
                            getValue: (v, _i) => v[series.Name] ?? 0,
                            xAxisId: "x",
                            yAxisId: "y",
                        } satisfies Series<Record<string, number>, "bar">;
                    }) ?? [])
            .setCategories(barChart.Series?.length
                ? barChart.Series[0].Data.map(x => x.Label)
                : [])
            .build(data) as ChartConfiguration<"bar">;
    }

    @LogInvocation(LogLevel.DEBUG)
    protected buildLineChart(lineChart: ICategoryChartData, builder: ChartJsConfigBuilder<Record<string, number>, "line">): ChartConfiguration<"line">
    {
        const data = this.transformCategoryData(lineChart);
        return builder
            .setTooltips({
                position: "average",
                callbacks: {
                    title: (tooltipItems: Array<TooltipItem<ChartJsType>>): string | string[] | void =>
                    {
                        return "";
                    },
                    label: (tooltipItem: TooltipItem<ChartJsType>): string | string[] =>
                    {
                        const dataSetLabel = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].label;
                        const value = tooltipItem.raw as number;
                        const amount = this.model.IsCurrency
                            ? formatCurrency(value, this.locale, "$")
                            : formatNumber(value, this.locale);
                        return `${dataSetLabel}: ${amount}`;
                    },
                },
            })
            .addScales(
                {
                    x: {
                        type: "linear",
                        // TODO(DO-8111) - support formatting ticks as currency
                    } satisfies DeepPartial<ScaleOptionsByType<"linear">>,
                    y: {
                        type: "linear",
                        min: lineChart.AxisMin
                            ?? Math.min(
                                ...data.flatMap(x =>
                                {
                                    return Object.values(x);
                                })),
                        max: this.getMaximum(lineChart, data),
                        // TODO(DO-8111) - support formatting ticks as currency
                    } satisfies DeepPartial<ScaleOptionsByType<"linear">>,
                })
            .addSeries(lineChart.Series.map(series =>
            {
                return {
                    name: series.Name,
                    type: "line",
                    getValue: (v, _i) => v[series.Name] ?? 0,
                } satisfies Series<Record<string, number>, "line">;
            }))
            .build(data) as ChartConfiguration<"line">;
    }

    @LogInvocation(LogLevel.DEBUG, true)
    protected transformCategoryData(chart: ICategoryChartData): Array<Record<string, number>>
    {
        const data: Array<Record<string, number>> = [];
        if (!chart.Series?.length)
        {
            return data;
        }

        const largestRow = Math.max(...chart.Series.map(x => x.Data.length));
        for (let i = 0; i < largestRow; i++)
        {
            const item: Record<string, number> = {};
            chart.Series.forEach(r =>
            {
                item[r.Name ?? ""] = (i <= r.Data.length)
                    ? r.Data[i]?.Value ?? 0
                    : 0;
            });

            data.push(item);
        }

        return data;
    }

    @LogInvocation(LogLevel.DEBUG, true)
    protected getMaximum(chart: ICategoryChartData, data: Array<Record<string, number>>): number
    {
        if (chart.AxisMax)
        {
            return chart.AxisMax;
        }

        if (isStacked(chart))
        {
            const sums = data.map(
                (datum) =>
                {
                    return Object.values(datum)
                        .reduce((d, value) => d + value, 0);
                });
            return Math.max(
                ...Object.values(sums));
        }

        return Math.max(
            ...data.flatMap(x =>
            {
                return Object.values(x);
            }));
    }

    @LogInvocation(LogLevel.DEBUG, true)
    protected getChartJsType(model: Readonly<IMetadataChart>): ChartJsType | undefined
    {
        if (!model)
        {
            return undefined;
        }

        switch (model.Type)
        {
            case MetadataChartType.Pie:
                return "pie";
            case MetadataChartType.Line:
                return "line";
            case MetadataChartType.Column:
            case MetadataChartType.Bar:
                return "bar";
            default:
                this.log.error(`unsupported chart type`, { type: model.Type ?? this.model.Type, model: this.model });
                throw new Error(`unsupported chart type; was "${model.Type ?? this.model.Type}"`);
        }
    }
}
